import { ListingInfo, NFT1155ListingInfo } from '@apeiron/common'
import { CurrencyTokenType } from '@apeiron/currency'
import { NextImage, useAccountContext } from '@apeiron/temp'
import { MuiCheckbox } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import EthereumSVG from '@public/icons/icon-ethereum.svg'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import PriceInputSection from '@src/components/share/trade/sellDialogView/TradeSellPriceInputView'
import TradeQuanatitySection from '@src/components/share/trade/TradeQuantitySection'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { INPUT_PROPS } from '@src/constants/layout'
import { SaleMethodType, SKIP_QUANTITY } from '@src/constants/trade'
import { FixedPriceSaleMethod, TradeParty } from '@src/types/trade'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import Decimal from 'decimal.js'
import * as R from 'ramda'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${props => props.theme.breakpoints.up('res480')} {
    padding: 0 44px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 0px;
  }
`

const DescriptionText = styled(Typography)`
  color: #838383;
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
  margin: 0 -2px;
`

const InfoText = styled(Typography)`
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin: -8px 0px 0px auto;
  text-align: right;
  width: 280px;
`

const SmallInfoText = styled(InfoText)`
  color: #cccccc;
  font-size: 10px;
  line-height: 10px;
`

const HighlightInfoText = styled(SmallInfoText)`
  color: #ffbf36;
  display: inline;
`

const CheckBoxContainer = styled(Box)`
  margin-bottom: 24px;
  display: flex;
  justify-items: center;
  align-items: center;
`

const CheckBoxDescription = styled(Box)`
  margin-left: 12px;
  font-size: 16px;
  line-height: 18px;
  color: #b1b3b3;
`

const StyledConfirmButton = styled(ApeironConfirmButton)`
  margin-top: 20px;
  margin-left: auto;
`

const EthereumIcon = styled(NextImage)`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 2px;
  margin-bottom: -2px;
`

const StyleCheckBox = styled(MuiCheckbox)`
  padding: 0;
`

const FixedPriceView: FC<Props> = (props: Props) => {
  const {
    checkboxRemark,
    className,
    isEditMode,
    listingInfo,
    loading,
    totalQuantity,
    tradeParties,
    onConfirm
  } = props

  const { account, fetchBalances } = useAccountContext()

  const { t } = useTranslation()

  const values = useMemo((): DefaultValues => {
    const duration = R.propOr(0, 'duration', listingInfo) as number

    return {
      fixedPrice: duration === 0 ? R.propOr(0, 'startPrice', listingInfo) : 0,
      quantity: duration === 0 ? R.propOr(1, 'quantity', listingInfo) : 1
    }
  }, [listingInfo])

  const [fixedPrice, setFixedPrice] = useState<FixedPriceSaleMethod>({
    type: SaleMethodType.FixedPrice,
    price: values.fixedPrice
  })

  const [quantity, setQuantity] = useState<number>(values.quantity)

  useEffect(() => {
    const { fixedPrice, quantity } = values

    setFixedPrice({
      type: SaleMethodType.FixedPrice,
      price: fixedPrice
    })
    setQuantity(quantity)
  }, [listingInfo, values])

  const [isChecked, setIsChecked] = useState(
    !featureAllowed(FeatureFlagType.ShowTradeRemark)
  )

  const isNegativePrice = fixedPrice.price <= 0

  const isEnoughQuantity =
    totalQuantity === SKIP_QUANTITY ||
    (quantity > 0 && quantity <= totalQuantity)

  const onCheckBoxChange = useCallback(() => {
    setIsChecked(prev => !prev)
  }, [])

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.currentTarget

      setFixedPrice(prev => ({ ...prev, [name]: Number(value) || 0 }))
    },
    []
  )

  const handleOnQuantityChange = useCallback((value: number) => {
    setQuantity(value)
  }, [])

  const onFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      // Blur the active element (text fields) to apply changes to filter
      const focusedElement = e.currentTarget.ownerDocument.activeElement

      if (focusedElement instanceof HTMLElement) {
        focusedElement.blur()
      }

      onConfirm?.(fixedPrice, quantity)
    },
    [fixedPrice, onConfirm, quantity]
  )

  const receivePrice = useMemo(() => {
    const feePercentage = R.sum(R.pluck('fee', tradeParties))

    const received = new Decimal(fixedPrice.price)
      .mul(new Decimal(100).minus(feePercentage))
      .div(100)
      .toNumber()

    return received
  }, [fixedPrice.price, tradeParties])

  useEffect(() => {
    const wethBalance = R.pathOr(
      0,
      ['balances', CurrencyTokenType.Weth, 'amount'],
      account
    )

    if (wethBalance === 0) {
      fetchBalances()
    }
  }, [account.walletAddress, account.balances])

  return (
    <Box className={className}>
      <Form onSubmit={onFormSubmit}>
        <DescriptionText>
          {t('dialog.sell.fixed_price.description')}
        </DescriptionText>
        {totalQuantity !== -1 && (
          <TradeQuanatitySection
            allowInput={!isEditMode}
            quantity={quantity}
            totalQuantity={totalQuantity}
            onChange={handleOnQuantityChange}
          />
        )}
        <PriceInputSection
          autoFocus
          defaultValue={fixedPrice.price}
          disabled={loading}
          inputProps={INPUT_PROPS.PLANET_SELLING_PRICE}
          label={t('dialog.sell.fixed_price.input.label')}
          name='price'
          onInputChange={onInputChange}
        />
        <InfoText as='span'>
          <Trans components={{ icon: <EthereumIcon src={EthereumSVG} /> }}>
            {t('dialog.sell.fixed_price.info', { price: receivePrice })}
          </Trans>
        </InfoText>
        {R.map((tradeParty: TradeParty) => {
          return (
            <SmallInfoText key={tradeParty.type}>
              <Trans
                components={{ highlight: <HighlightInfoText as='span' /> }}
              >
                {t(`dialog.sell.fee.${tradeParty.type}`, {
                  feePercentage: tradeParty.fee
                })}
              </Trans>
            </SmallInfoText>
          )
        }, tradeParties)}
        {featureAllowed(FeatureFlagType.ShowTradeRemark) && (
          <CheckBoxContainer>
            <StyleCheckBox
              checked={isChecked}
              disabled={loading}
              onChange={onCheckBoxChange}
            />
            <CheckBoxDescription>{checkboxRemark}</CheckBoxDescription>
          </CheckBoxContainer>
        )}
        <StyledConfirmButton
          disabled={!isChecked || isNegativePrice || !isEnoughQuantity}
          loading={loading}
          type='submit'
        />
      </Form>
    </Box>
  )
}

type DefaultValues = {
  fixedPrice: number
  quantity: number
}

type Props = {
  checkboxRemark?: string
  className?: string
  isEditMode: boolean
  listingInfo?: ListingInfo | NFT1155ListingInfo | null
  loading?: boolean
  totalQuantity: number
  tradeParties: TradeParty[]
  onConfirm?: (saleMethod: FixedPriceSaleMethod, quantity: number) => void
}

export default FixedPriceView
